import React from 'react';
import { useTranslation } from 'next-i18next';
import { useGetQuizUserInfo } from '@/api/gen/quiz-user/quiz-user.gen';
import { EnterTracking } from '@/components/EnterTracking';
import { useUserQuizes } from '@/module/profile/user/hooks/useFetchUserQuizes';
import { UserQuizList } from '@/module/profile/user/UserQuizList';
import { PLAIN_FRAME_BODY_CLASS_NAME, ProductContentFrame } from '@/shared/components/ProductContentFrame';
import { SectionHeader } from '@/shared/components/SectionHeader';
import { UserProfile } from '@/shared/features/user-profile';
import { PathParentLinkButton } from '@/shared/modules/navigation-path';
import { ListLayoutContainer, ListLayoutWrapper } from '@/shared/quizList/Layout';
import { SearchInputQuizForm } from '@/shared/quizList/SearchField';
import { type QuizListSearchProps, SearchMetaBar } from '@/shared/quizList/SearchField/SearchMetaBar';
import { UserFolderSection } from './components/UserFolderSection';
import { useUserFolderPreview } from './hooks/useUserFolderPreview';
import { useUserProfileLayout } from './hooks/useUserProfileLayout';
import { useProfileSidebarMenu } from './shared/hooks/useProfileSidebarMenu';

/**
 * 남의 프로필. 내 퀴즈와 같은 구성이다 — 검색바 하나 아래에 공개 폴더 섹션, 그 아래 공개 퀴즈 섹션.
 * 무엇을 그릴지는 `useUserProfileLayout` 이 두 목록을 함께 보고 정한다.
 */
export const UserProfilePageView = ({
  userHashId
}: {
  userHashId: string;
}) => {
  const {
    data
  } = useGetQuizUserInfo(userHashId);
  const {
    t
  } = useTranslation(['common']);
  const {
    totalCount,
    isLoading: isQuizzesLoading
  } = useUserQuizes(userHashId);
  const {
    isLoading: isFoldersLoading
  } = useUserFolderPreview(userHashId);
  const layout = useUserProfileLayout(userHashId);
  useProfileSidebarMenu(userHashId, data?.data?.username, `/profile/${userHashId}`);
  // 하이드레이션되기에 발생하지않는다
  if (!data?.data) return null;
  const quizCount = t('common:formatNumber.count', {
    n: totalCount.toLocaleString()
  });
  return <EnterTracking eventName={'enter_user_profile'} eventProperties={{
    userEmail: data.data.userEmail,
    username: data.data.username
  }} data-sentry-element="EnterTracking" data-sentry-component="UserProfilePageView" data-sentry-source-file="UserProfilePageView.tsx">
      {/* 외부 플랫폼에서는 이 화면을 열지 않는다(index.page 가 되돌린다). */}
      {/* 목록 버튼은 걸어온 길의 부모 칸으로, 길이 없으면 내 프로필이면 내 퀴즈·아니면 자료실로 간다.
          소유를 모르는 동안은 같은 크기의 빈 자리만 둬 본문이 밀리지 않는다. */}
      <ProductContentFrame
    // 폴더 섹션이 퀴즈 목록 위에 있어 둘 다 받아야 돌아갈 위치가 맞는다.
    ready={!isQuizzesLoading && !isFoldersLoading} hasBanner={false} parentLink={<PathParentLinkButton placement="plain" />} data-sentry-element="ProductContentFrame" data-sentry-source-file="UserProfilePageView.tsx">
        <ListLayoutWrapper className={PLAIN_FRAME_BODY_CLASS_NAME} data-sentry-element="ListLayoutWrapper" data-sentry-source-file="UserProfilePageView.tsx">
          <ListLayoutContainer className={'max-w-none gap-none max-tablet-sm:gap-none'} data-sentry-element="ListLayoutContainer" data-sentry-source-file="UserProfilePageView.tsx">
            <div className={'flex w-full flex-col gap-md'}>
              <UserProfile email={null} isAmbassador={data.data.isAmbassador} userHashId={userHashId} name={data?.data.username} avatarData={data.data.avatarData} data-sentry-element="UserProfile" data-sentry-source-file="UserProfilePageView.tsx" />
              {/* 개수는 섹션 제목마다 있다. 검색바에 또 두면 겹친다. */}
              <SearchField eventType={'user'} data-sentry-element="SearchField" data-sentry-source-file="UserProfilePageView.tsx" />
            </div>
            <div className={'flex w-full flex-col'}>
              {layout.folders && <UserFolderSection userHashId={userHashId} />}
              {layout.quizzes && <>
                  <SectionHeader title={t('common:folder.label.quizzes')} count={quizCount} />
                  <UserQuizList userHashId={userHashId} userEmail={data.data.userEmail} username={data.data.username} countryCode={data?.data.countryCode ?? 'EN'} />
                </>}
            </div>
          </ListLayoutContainer>
        </ListLayoutWrapper>
      </ProductContentFrame>
    </EnterTracking>;
};
const SearchField = (props: QuizListSearchProps) => {
  const {
    t
  } = useTranslation(['common']);
  return <div className={'flex items-center justify-between gap-xs pb-xs'} data-sentry-component="SearchField" data-sentry-source-file="UserProfilePageView.tsx">
      <SearchInputQuizForm {...props} placeholder={t('public.search.placeholder')} data-sentry-element="SearchInputQuizForm" data-sentry-source-file="UserProfilePageView.tsx" />
      <SearchMetaBar {...props} data-sentry-element="SearchMetaBar" data-sentry-source-file="UserProfilePageView.tsx" />
    </div>;
};